import { Cake, Celebration, EmojiEvents } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { motion } from 'framer-motion';
import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import BirthdayList from '../components/BirthdayList.tsx';
import GoogleSignInButton from '../components/GoogleSignInButton.tsx';
import Header from '../components/Header.tsx';
import { useUser } from '../hooks/useUser.ts';

// Animation variants for elements
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1, 
    transition: { 
      when: "beforeChildren", 
      staggerChildren: 0.3,
      duration: 0.5
    } 
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { 
    y: 0, 
    opacity: 1,
    transition: { duration: 0.5 }
  }
};

// Custom animation variants for icons
const floatingVariants = {
  float: {
    y: [0, -3, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      repeatType: "mirror" as const,
      ease: "easeInOut"
    }
  }
};

const pulsingVariants = {
  pulse: {
    scale: [0.9, 1.1, 0.9],
    transition: { 
      duration: 3,
      repeat: Infinity,
      repeatType: "mirror" as const
    }
  }
};

const waveVariants = {
  wave: {
    rotate: [0, 5, -5, 0],
    transition: { 
      duration: 2.5, 
      repeat: Infinity,
      repeatType: "mirror" as const,
      ease: "easeInOut"
    }
  }
};

const Index = () => {
  const { isLoading, data: user } = useUser();
  const [showAuthSuccess, setShowAuthSuccess] = useState(false);
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  
  useEffect(() => {
    // Check for auth_success parameter in URL
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('auth_success') === 'true') {
      setShowAuthSuccess(true);
      
      // Clean up the URL by removing the auth_success parameter (but keep other params)
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('auth_success');
      const newSearch = newSearchParams.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
      window.history.replaceState({}, document.title, newUrl);
    }
    
    // Check for person parameter in URL
    const personId = searchParams.get('person');
    if (personId) {
      setSelectedPersonId(personId);
    }
  }, [location.search]);
  
  const handleCloseSnackbar = (event?: SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowAuthSuccess(false);
  };

  return (
    <>
      {/* Fixed Header */}
      <Header />
      
      {/* Main Content with padding to account for fixed header */}
      <Box 
        component={motion.div}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        sx={{ 
          bgcolor: 'background.default', 
          minHeight: '100vh', 
          pt: { xs: '64px', sm: '68px' }, 
          pb: 4,
          px: { xs: 1, sm: 2 },
          display: 'flex',
          flexDirection: 'column',
          WebkitOverflowScrolling: 'touch',
          background: 'linear-gradient(135deg, #F9FAFB 0%, #F0F8FF 100%)',
        }}
      >
        <Container maxWidth="xl" sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          flexGrow: 1
        }}>
          {!user && !isLoading && (
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '70vh',
              textAlign: 'center',
              gap: 4
            }}>
              <motion.div
                variants={itemVariants}
                style={{ width: '100%', maxWidth: 550 }}
              >
                <Paper 
                  elevation={4}
                  sx={{ 
                    p: 5, 
                    width: '100%',
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, #FFFFFF 0%, #FEFEFE 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    position: 'relative',
                    overflow: 'hidden',
                    border: '1px solid rgba(36, 100, 235, 0.1)'
                  }}
                >
                  {/* Decorative elements */}
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      top: -20, 
                      right: -20, 
                      width: 120, 
                      height: 120, 
                      borderRadius: '50%', 
                      background: 'rgba(36, 100, 235, 0.05)',
                      zIndex: 0
                    }} 
                  />
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      bottom: -30, 
                      left: -30, 
                      width: 180, 
                      height: 180, 
                      borderRadius: '50%', 
                      background: 'rgba(255, 213, 79, 0.05)',
                      zIndex: 0
                    }} 
                  />
                  
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'center',
                      mb: 2, 
                      zIndex: 1
                    }}
                  >
                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                      }}
                    >
                      <motion.div variants={floatingVariants} animate="float">
                        <Cake sx={{ color: theme.palette.primary.main, fontSize: 40 }} />
                      </motion.div>
                      <motion.div variants={pulsingVariants} animate="pulse">
                        <Celebration sx={{ color: '#FFD54F', fontSize: 40 }} />
                      </motion.div>
                      <motion.div variants={waveVariants} animate="wave">
                        <EmojiEvents sx={{ color: theme.palette.warning.main, fontSize: 40 }} />
                      </motion.div>
                    </Box>
                  </Box>
                  
                  <Typography 
                    variant="h3" 
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      maxWidth: 400,
                      zIndex: 1,
                      lineHeight: 1.5
                    }}
                  >
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.7, delay: 0.5 }}
                    >
                      Never miss a special day again! Keep track of birthdays and plan perfect celebrations.
                    </motion.span>
                  </Typography>
                  
                  <Box 
                    sx={{ 
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      zIndex: 1
                    }}
                  >
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 100, delay: 0.7 }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <GoogleSignInButton fullWidth={false} size="large" />
                    </motion.div>
                  </Box>
                </Paper>
              </motion.div>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box 
              component={motion.div}
              variants={itemVariants}
              sx={{ 
                flex: { xs: '1 1 100%', md: '2 1 0%' },
                position: 'relative',
                zIndex: 1
              }}
            >
              {user && (<BirthdayList initialSelectedPersonId={selectedPersonId} /> )}
            </Box>
          </Box>
          
        </Container>
      </Box>
    </>
  );
};

export default Index;
